import { Component, Inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { AssessmentCentralService } from "../../services/assessment-central.service";

interface ResponseChoice {
  response: string;
  response_description: string;
}

interface EditableRecord {
  context: string;
  definition_of_done: string;
  response_choices: ResponseChoice[];
  expanded?: boolean;
}

interface EditableQuestion {
  question: string;
  records: EditableRecord[];
  expanded?: boolean;
}

interface EditableGroup {
  group: string;
  questions: EditableQuestion[];
  expanded?: boolean;
}

interface Selection {
  groupIndex: number | null;
  questionIndex: number | null;
}

interface RequiredDocument {
  name: string;
}

interface ResponseChoice {
  response: string;
  response_description: string;
}

interface EditableRecord {
  context: string;
  definition_of_done: string;
  response_choices: ResponseChoice[];
  required_documents: RequiredDocument[];
  expanded?: boolean;
}

@Component({
  selector: "app-assessment-template-edit-dialog",
  templateUrl: "./assessment-template-edit-dialog.component.html",
  styleUrls: ["./assessment-template-edit-dialog.component.scss"],
})
export class AssessmentTemplateEditDialogComponent {
  categories: any;
  modalHeader: string = "";
  isCreateMode: boolean = false;
  template: any = {};
  step: 1 | 2 = 2;
  assessmentTags: any;

  groups: EditableGroup[] = [];
  saving = false;
  selected: Selection = {
    groupIndex: null,
    questionIndex: null,
  };

  templateForm = new FormGroup({
    name: new FormControl("", [Validators.required]),
    description: new FormControl("", [Validators.required]),
    applicability: new FormControl(""),
    scoring_methodology: new FormControl(""),
    assessment_scoring_scale: new FormControl(""),
    category: new FormControl("", [Validators.required]),
  });

  constructor(
    public dialogRef: MatDialogRef<AssessmentTemplateEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly assessmentCentralService: AssessmentCentralService,
    private readonly toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.isCreateMode = this.data?.isCreateMode === true;
    this.template = this.data?.template;

    if (this.isCreateMode) {
      this.step = 1;
      this.getCategories();
      if (!this.groups.length) {
        this.addGroup();
      }
      this.modalHeader = "Create Assessment Template";
    } else {
      this.groups = this.groupQuestions(this.template?.questions || []);
      if (!this.groups.length) {
        this.addGroup();
      }
      this.groups.forEach((g) => (g.expanded = true));
      this.groups.forEach((g) =>
        g.questions.forEach((q) => (q.expanded = true))
      );
      this.selectFirstAvailable();
      this.step = 2;
      this.modalHeader = `Edit Template — ${this.template?.name}`;
    }
  }

  getCategories() {
    this.assessmentCentralService
      .getAssessmentTemplateCategory()
      .subscribe((response) => {
        this.categories = response;
      });
    this.categories = [];
  }

  // ---------- building tree from an existing template (edit mode) ----------

  private groupQuestions(questions: any[]): EditableGroup[] {
    const groups: EditableGroup[] = [];
    questions.forEach((q: any) => {
      const record: EditableRecord = {
        context: q.context || "",
        definition_of_done: q.definition_of_done || "",
        response_choices: this.normalizeChoices(q.response_choices),
        required_documents: this.normalizeRequiredDocuments(
          q.required_documents
        ),
        expanded: true,
      };

      let group = groups.find((g) => g.group === q.group_name);
      if (!group) {
        group = { group: q.group_name, questions: [], expanded: true };
        groups.push(group);
      }

      let question = group.questions.find((qq) => qq.question === q.question);
      if (!question) {
        question = { question: q.question, records: [], expanded: true };
        group.questions.push(question);
      }

      question.records.push(record);
    });
    return groups;
  }

  private normalizeChoices(choices: any): ResponseChoice[] {
    if (!Array.isArray(choices)) {
      return [];
    }
    return choices.map((c: any) => ({
      response: c.response ?? "",
      response_description: c.response_description ?? "",
    }));
  }

  private normalizeRequiredDocuments(docs: any): RequiredDocument[] {
    if (!Array.isArray(docs)) {
      return [];
    }
    return docs.map((d: any) => ({
      name: d.name ?? "",
    }));
  }

  // ---------- step navigation ----------

  goNext() {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      this.toastrService.error(
        "Please fill in all required fields before continuing"
      );
      return;
    }
    if (!this.groups.length) {
      this.addGroup();
    }
    this.step = 2;
  }

  goBack() {
    this.step = 1;
  }

  // ---------- empty-entity factories ----------

  private emptyRecord(): EditableRecord {
    return {
      context: "",
      definition_of_done: "",
      response_choices: [{ response: "", response_description: "" }],
      required_documents: [],
      expanded: true,
    };
  }

  private emptyQuestion(): EditableQuestion {
    return {
      question: "",
      records: [this.emptyRecord()],
      expanded: true,
    };
  }

  addRequiredDocument(record: EditableRecord) {
    record.required_documents.push({ name: "" });
  }

  removeRequiredDocument(record: EditableRecord, docIndex: number) {
    record.required_documents.splice(docIndex, 1);
  }

  private selectFirstAvailable() {
    for (let gi = 0; gi < this.groups.length; gi++) {
      const g = this.groups[gi];
      for (let qi = 0; qi < g.questions.length; qi++) {
        const q = g.questions[qi];
        if (q.records.length) {
          this.selected = { groupIndex: gi, questionIndex: qi };
          return;
        }
      }
    }
    this.selected = { groupIndex: null, questionIndex: null };
  }

  getChildTags(data: any) {
    this.assessmentTags = data;
  }

  // ---------- tree structure edits ----------

  toggleGroup(group: EditableGroup) {
    group.expanded = !group.expanded;
  }

  toggleQuestion(question: EditableQuestion) {
    question.expanded = !question.expanded;
  }

  addGroup() {
    this.groups.push({
      group: "",
      questions: [this.emptyQuestion()],
      expanded: true,
    });
    const gi = this.groups.length - 1;
    this.selected = { groupIndex: gi, questionIndex: 0 };
  }

  removeGroup(groupIndex: number) {
    this.groups.splice(groupIndex, 1);
    if (this.selected.groupIndex === groupIndex) {
      this.selectFirstAvailable();
    } else if (
      this.selected.groupIndex !== null &&
      this.selected.groupIndex > groupIndex
    ) {
      this.selected.groupIndex--;
    }
  }

  addQuestion(group: EditableGroup, groupIndex: number) {
    group.questions.push(this.emptyQuestion());
    group.expanded = true;
    this.selected = { groupIndex, questionIndex: group.questions.length - 1 };
  }

  removeQuestion(
    group: EditableGroup,
    groupIndex: number,
    questionIndex: number
  ) {
    group.questions.splice(questionIndex, 1);
    if (
      this.selected.groupIndex === groupIndex &&
      this.selected.questionIndex === questionIndex
    ) {
      this.selectFirstAvailable();
    }
  }

  selectQuestion(groupIndex: number, questionIndex: number) {
    this.selected = { groupIndex, questionIndex };
  }

  isSelected(groupIndex: number, questionIndex: number): boolean {
    return (
      this.selected.groupIndex === groupIndex &&
      this.selected.questionIndex === questionIndex
    );
  }

  get selectedRecord(): EditableRecord | null {
    const { groupIndex, questionIndex } = this.selected;
    if (groupIndex === null || questionIndex === null) {
      return null;
    }
    return (
      this.groups[groupIndex]?.questions[questionIndex]?.records[0] || null
    );
  }

  get selectedGroup(): EditableGroup | null {
    return this.selected.groupIndex === null
      ? null
      : this.groups[this.selected.groupIndex] || null;
  }

  get selectedQuestion(): EditableQuestion | null {
    if (
      this.selected.groupIndex === null ||
      this.selected.questionIndex === null
    ) {
      return null;
    }
    return (
      this.groups[this.selected.groupIndex]?.questions[
        this.selected.questionIndex
      ] || null
    );
  }

  // ---------- response edits (right pane) ----------

  addResponseChoice(record: EditableRecord) {
    record.response_choices.push({ response: "", response_description: "" });
  }

  removeResponseChoice(record: EditableRecord, choiceIndex: number) {
    record.response_choices.splice(choiceIndex, 1);
  }

  // ---------- validation (surfaced as indicators in the tree) ----------

  groupHasError(group: EditableGroup): boolean {
    if (!group.group?.trim() || !group.questions.length) {
      return true;
    }
    return group.questions.some((q) => this.questionHasError(q));
  }

  questionHasError(question: EditableQuestion): boolean {
    if (!question.question?.trim() || !question.records.length) {
      return true;
    }
    return question.records.some((r) => this.recordHasError(r));
  }

  recordHasError(record: EditableRecord): boolean {
    return (
      !record.response_choices.length ||
      record.response_choices.some((c) => !c.response?.trim()) ||
      record.required_documents.some((d) => !d.name?.trim())
    );
  }

  recordLabel(record: EditableRecord): string {
    return record.context?.trim() || "New context";
  }

  isValid(): boolean {
    if (!this.groups.length) {
      return false;
    }
    return !this.groups.some((g) => this.groupHasError(g));
  }

  // ---------- save ----------

  save() {
    if (!this.isValid()) {
      this.toastrService.error(
        "Group, Question, and at least one Response are required for every entry"
      );
      return;
    }

    const flatQuestions: any[] = [];
    this.groups.forEach((group) => {
      group.questions.forEach((question) => {
        question.records.forEach((r) => {
          flatQuestions.push({
            group: group.group.trim(),
            question: question.question.trim(),
            context: r.context.trim(),
            definition_of_done: r.definition_of_done.trim(),
            response_choices: r.response_choices.map((c) => ({
              response: c.response.trim(),
              response_description: (c.response_description || "").trim(),
            })),
            required_documents: r.required_documents
              .map((d) => ({ name: d.name.trim() }))
              .filter((d) => d.name),
          });
        });
      });
    });

    this.saving = true;

    if (!this.isCreateMode) {
      const editPayload = { questions: flatQuestions };
      this.assessmentCentralService
        .updateAssessmentTemplate(this.template.id, editPayload)
        .subscribe(
          (response: any) => {
            this.saving = false;
            this.dialogRef.close(response);
          },
          (error: any) => {
            this.saving = false;
            this.toastrService.error(
              error?.error?.error || "Failed to update assessment template"
            );
          }
        );
      return;
    }

    const formValue = this.templateForm.value;
    const payload: any = {
      name: formValue.name,
      description: formValue.description,
      applicability: formValue.applicability,
      scoring_methodology: formValue.scoring_methodology,
      assessment_scoring_scale: formValue.assessment_scoring_scale,
      category: formValue.category,
      questions: flatQuestions,
      selectedTags: this.assessmentTags,
    };

    this.assessmentCentralService
      .createAssessmentTemplateFromJson(payload)
      .subscribe(
        (response: any) => {
          this.saving = false;
          this.dialogRef.close({ response, selectedTags: this.assessmentTags });
        },
        (error: any) => {
          this.saving = false;
          this.toastrService.error(
            error?.error?.error || "Failed to create assessment template"
          );
        }
      );
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
